import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_GridMaker = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/grid-maker',
			name: 'Grid Maker \u2014 Feed Puzzle Creator | Lift',
			description:
				"Turn any photo into a perfect grid. Set rows and columns, preview live, and download all tiles as a ZIP. Try Lift's grid maker online \u2014 no watermarks, no installs.",
			url: 'https://liftapp.ai/grid-maker',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/grid-maker#product' },
				{ '@id': 'https://liftapp.ai/grid-maker#howto' },
				{ '@id': 'https://liftapp.ai/grid-maker#benefits' },
				{ '@id': 'https://liftapp.ai/grid-maker#faq' },
				{ '@id': 'https://liftapp.ai/grid-maker#why-lift' },
				{ '@id': 'https://liftapp.ai/grid-maker#explore-tools' },
				{ '@id': 'https://liftapp.ai/grid-maker#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/grid-maker#product',
			name: 'Grid Maker \u2014 Design Your Feed One Photo at a Time',
			description:
				"Turn any photo into a perfect grid with Lift's Grid Maker. Set rows and columns, preview the grid live, and download all tiles as a ZIP archive in one click.",
			url: 'https://liftapp.ai/grid-maker',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/grid-maker#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: "Choose a photo from your device or take one using Lift's app.",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Set your grid',
					text: 'Use the Rows and Columns controls to define your grid. The dotted overlay updates in real time on your photo \u2014 no guessing required.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Divide the Image',
					text: 'Click \u201cSplit\u201d. The single photo view is instantly replaced by all individual tiles displayed in a grid layout \u2014 ready to review and re-adjust if needed.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download all parts',
					text: 'Click Download ZIP to save every tile in one archive. Need a different grid? Adjust the settings and divide the image again at any time.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/grid-maker#benefits',
			name: 'Who can benefit from the Lift Grid Maker?',
			description:
				"Lift's Grid Maker is ideal for Profile users, content creators, designers, and everyday users who want to turn a single photo into a seamless profile grid.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Profile users can create a seamless mosaic across their profile by posting each tile as a separate photo. A 3x3 or 9-post grid turns a single image into a scroll-stopping layout.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content creators can prepare multi-panel carousel posts or panoramic feed layouts with consistent tile sizing.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers can export precisely equal tiles from any source image for use in multi-panel mockups, print layouts, or digital asset libraries.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Mobile users can adjust rows and columns with simple +/\u2013 taps, split in seconds, and download all parts as a single ZIP.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday users can split any photo into equal parts online without installing software.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift Grid Maker \u2014 photo divided into grid tiles',
				description:
					'Grid Maker \u2014 Movie poster divided into 9 equal grid tiles in Lift with a ZIP download arrow showing the export result.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/grid-maker#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Grid Maker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Grid Maker is a photo utility built into the Lift editor that divides a single photo into equal grid tiles sized for social posts. The result is a set of perfectly matched parts you can download as a ZIP archive.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Grid Maker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Simply upload your photo from your device, adjust the grid using \u2013 and + buttons, click \u201cSplit\u201d to generate the tiles, and Download ZIP to save them all at once.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I choose how many parts to divide my image into?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Grid Maker lets you set both Rows and Columns independently using \u2013 and + controls, so you can divide your photo into any grid size you need.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I preview the grid before splitting?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. As soon as you open the tool, a grid is drawn over your photo. It updates in real time with every Rows or Columns change \u2014 before you click \u201cSplit\u201d.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/grid-maker#why-lift',
			name: 'Why use Lift Grid Maker',
			description:
				"Lift's Grid Maker combines ease of use, live grid preview, and instant ZIP download \u2014 making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift Grid Maker \u2013 Before and After 3x3 Photo Grid Result',
				description:
					'Before and after view of a city photo split into a seamless 3x3 grid using Lift.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/grid-maker#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more \u2013 all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/grid-maker#all-in-one',
			name: 'Try Lift \u2013 AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Grid Maker',
					item: 'https://liftapp.ai/grid-maker',
				},
			],
		},
	],
};
